import { NGXLogger } from 'ngx-logger';
import { Injectable, inject } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { APP_LANGUAGES, AppLanguage, TRANSLATIONS } from "./translations";

const STORAGE_KEY = "tms-language";
const ENGLISH_VALUE_TO_KEY = new Map(
  Object.entries(TRANSLATIONS.en).map(([key, value]) => [value, key]),
);


@Injectable({
 providedIn: "root" })
export class TranslationService {
  private readonly logger = inject(NGXLogger);

  readonly languages = APP_LANGUAGES;
  private readonly languageSubject = new BehaviorSubject<AppLanguage>(
    this.readInitialLanguage(),
  );

  readonly language$ = this.languageSubject.asObservable();

  constructor() {
    // Keep <html lang> in step with the chosen language: screen readers, browser translate prompts and
    // search engines read it (it used to stay "en" for Hindi, Portuguese, French and Spanish users).
    this.languageSubject.subscribe((language) => {
      document.documentElement.lang = language;
    });
  }

  get currentLanguage(): AppLanguage {
    return this.languageSubject.value;
  }

  setLanguage(language: AppLanguage): void {
    const nextLanguage = TRANSLATIONS[language] ? language : "en";
    localStorage.setItem(STORAGE_KEY, nextLanguage);
    this.logger.debug("i18n language changed", { language: nextLanguage });
    this.languageSubject.next(nextLanguage);
  }

  instant(key: string | null | undefined): string {
    if (key === null || key === undefined || key === "") {
      return key ?? "";
    }

    const englishKey =
      TRANSLATIONS.en[key] !== undefined ? key : ENGLISH_VALUE_TO_KEY.get(key);

    return englishKey
      ? TRANSLATIONS[this.currentLanguage][englishKey] ?? TRANSLATIONS.en[englishKey]
      : key;
  }

  private readInitialLanguage(): AppLanguage {
    const storedLanguage = localStorage.getItem(STORAGE_KEY) as AppLanguage | null;
    return storedLanguage && TRANSLATIONS[storedLanguage] ? storedLanguage : "en";
  }
}
